import {
  ColumnDef,
  ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  Table,
  useReactTable,
} from '@tanstack/react-table'
import { observer } from 'mobx-react-lite'
import * as React from 'react'
import { Data, UserPersonalSkillsTableRow } from '../data'
import { fuzzyFilter } from 'app/packs/src/utils/table-helpers'
import { SkeletonCell } from 'components/table/skeleton'

type TableContextProps = {
  columnFilters: ColumnFiltersState
  setColumnFilters: (value: ColumnFiltersState) => void
  data: UserPersonalSkillsTableRow[]
  globalFilter: string
  setGlobalFilter: (value: string) => void
  table: Table<UserPersonalSkillsTableRow>
  loading: boolean
  selectedSkillId: string | null
  setSelectedSkillId: (id: string | null) => void
  onDeleteSkill: (id: string) => void
  onViewSkill: (id: string) => void
}

const TableContext = React.createContext<TableContextProps | null>(null)

export const TableProvider = observer(
  ({
    loading = false,
    children,
    data,
    onDeleteSkill,
    onViewSkill,
  }: {
    children: React.ReactNode
    loading?: boolean
    data: UserPersonalSkillsTableRow[]
    onDeleteSkill: (id: string) => void
    onViewSkill: (id: string) => void
  }) => {
    const [columnFilters, setColumnFilters] =
      React.useState<ColumnFiltersState>([])

    const [globalFilter, setGlobalFilter] = React.useState('')
    const [selectedSkillId, setSelectedSkillId] = React.useState<string | null>(
      null
    )
    const vm = React.useMemo(() => new Data(), [])

    const columns = React.useMemo(() => {
      const cols = vm.columns
      if (!loading) return cols

      return cols.map((col) => {
        if (col.id && ['name', 'actions'].includes(col.id)) return col
        return {
          ...col,
          cell: SkeletonCell,
        } as ColumnDef<UserPersonalSkillsTableRow, unknown>
      })
    }, [vm, loading])

    const table = useReactTable({
      columns,
      data,
      filterFns: { fuzzy: fuzzyFilter },
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getSortedRowModel: getSortedRowModel(),
      onColumnFiltersChange: setColumnFilters,
      onGlobalFilterChange: setGlobalFilter,
      getPaginationRowModel: getPaginationRowModel(),
      initialState: {
        sorting: [{ id: 'name', desc: false }],
        pagination: {
          pageSize: 15,
          pageIndex: 0,
        },
        columnVisibility: {
          source: false,
        },
      },
      state: {
        columnFilters,
        globalFilter,
      },
    })

    const contextValue = {
      columnFilters,
      setColumnFilters,
      data,
      globalFilter,
      setGlobalFilter,
      table,
      loading,
      selectedSkillId,
      setSelectedSkillId,
      onDeleteSkill,
      onViewSkill,
    }

    return (
      <TableContext.Provider value={contextValue}>
        {children}
      </TableContext.Provider>
    )
  }
)

export const useTable = () => {
  const context = React.useContext(TableContext)

  if (context === null) {
    throw new Error('useTable must be used within a TableProvider')
  }

  return context
}
